import {
  PreviewTask,
  TaskDefaultStatusId,
  TaskLabel,
} from "@jugl-web/rest-api/tasks/types";
import { TaskBoardModel } from "@jugl-web/rest-api/tasks/models/TaskBoard";
import { TaskCustomFieldModel } from "@jugl-web/rest-api/tasks/models/TaskCustomField";
import { TaskCustomStatusDto } from "@jugl-web/rest-api/tasks/models/TaskCustomStatus";
import {
  TaskSorting,
  TaskSortingOrder,
} from "../../storage/useTaskClientDrivenSortingStorage";
import { TaskDefaultFieldId } from "../../types";
import { FALLBACK_PENDING_STATUS } from "../../consts";

export const getSortByTaskIdComparer =
  (order: TaskSortingOrder) => (taskA: PreviewTask, taskB: PreviewTask) => {
    const result = taskA.id.localeCompare(taskB.id);
    return order === "asc" ? result : -result;
  };

export const getSortByTaskNameComparer =
  (order: TaskSortingOrder) => (taskA: PreviewTask, taskB: PreviewTask) => {
    const result = taskA.name.localeCompare(taskB.name);
    return order === "asc" ? result : -result;
  };

export const getSortByLabelComparer =
  (order: TaskSortingOrder, labels: TaskLabel[]) =>
  (taskA: PreviewTask, taskB: PreviewTask) => {
    const labelA = labels.find((l) => l.id === taskA.label_id);
    const labelB = labels.find((l) => l.id === taskB.label_id);

    const textA = labelA?.text || "";
    const textB = labelB?.text || "";

    // Handle empty values: bottom for asc, top for desc
    if (!textA && !textB) return 0;
    if (!textA) return order === "asc" ? 1 : -1;
    if (!textB) return order === "asc" ? -1 : 1;

    const result = textA.toLowerCase().localeCompare(textB.toLowerCase());
    return order === "asc" ? result : -result;
  };

export const getSortByStatusNamesComparer =
  (
    order: TaskSortingOrder,
    getStatusByBoard: (
      boardId: string | null,
      statusId: string
    ) => TaskCustomStatusDto | undefined
  ) =>
  (taskA: PreviewTask, taskB: PreviewTask) => {
    // Seeks for statuses in respective boards
    // Falls back to the board's pending status if matching status not found
    // Falls back to the fallback pending status if board's pending status not found
    const statusA =
      getStatusByBoard(taskA.board_id, taskA.status) ||
      getStatusByBoard(taskA.board_id, TaskDefaultStatusId.pending) ||
      FALLBACK_PENDING_STATUS;

    const statusB =
      getStatusByBoard(taskB.board_id, taskB.status) ||
      getStatusByBoard(taskB.board_id, TaskDefaultStatusId.pending) ||
      FALLBACK_PENDING_STATUS;

    const result = statusA.text
      .toLowerCase()
      .localeCompare(statusB.text.toLowerCase());

    return order === "asc" ? result : -result;
  };

export const getSortByPriorityComparer =
  (order: TaskSortingOrder) => (taskA: PreviewTask, taskB: PreviewTask) => {
    const priorityOrder = { none: 3, low: 0, medium: 1, high: 2 };
    const priorityA = priorityOrder[taskA.priority] ?? 0;
    const priorityB = priorityOrder[taskB.priority] ?? 0;
    const result = priorityA - priorityB;
    return order === "asc" ? result : -result;
  };

export const getSortByCreatedAtComparer =
  (order: TaskSortingOrder) => (taskA: PreviewTask, taskB: PreviewTask) => {
    const dateA = new Date(taskA.created_at).valueOf();
    const dateB = new Date(taskB.created_at).valueOf();
    const result = dateA - dateB;
    return order === "asc" ? result : -result;
  };

export const getSortByUpdatedAtComparer =
  (order: TaskSortingOrder) => (taskA: PreviewTask, taskB: PreviewTask) => {
    const dateA = new Date(taskA.updated_at).valueOf();
    const dateB = new Date(taskB.updated_at).valueOf();
    const result = dateA - dateB;
    return order === "asc" ? result : -result;
  };

export const getSortByAssigneeComparer =
  (order: TaskSortingOrder) => (taskA: PreviewTask, taskB: PreviewTask) => {
    const assigneeA = taskA.assignees[0] || "";
    const assigneeB = taskB.assignees[0] || "";

    if (!assigneeA && !assigneeB) return 0;
    if (!assigneeA) return order === "asc" ? 1 : -1;
    if (!assigneeB) return order === "asc" ? -1 : 1;

    const result = assigneeA.localeCompare(assigneeB);
    return order === "asc" ? result : -result;
  };

export const getSortByCustomerComparer =
  (order: TaskSortingOrder) => (taskA: PreviewTask, taskB: PreviewTask) => {
    const customerA = taskA.customer?.first_name || "";
    const customerB = taskB.customer?.first_name || "";

    if (!customerA && !customerB) {
      return 0;
    }
    if (!customerA) {
      return order === "asc" ? 1 : -1;
    }
    if (!customerB) {
      return order === "asc" ? -1 : 1;
    }

    const result = customerA.localeCompare(customerB);
    return order === "asc" ? result : -result;
  };

export const getSortByBoardComparer =
  (order: TaskSortingOrder, boards: TaskBoardModel[]) =>
  (taskA: PreviewTask, taskB: PreviewTask) => {
    const boardA = boards.find((board) => board.id === taskA.board_id);
    const boardB = boards.find((board) => board.id === taskB.board_id);

    const nameA = boardA?.name || "";
    const nameB = boardB?.name || "";

    if (!nameA && !nameB) return 0;
    if (!nameA) return order === "asc" ? 1 : -1;
    if (!nameB) return order === "asc" ? -1 : 1;

    const result = nameA.localeCompare(nameB);
    return order === "asc" ? result : -result;
  };

export const getSortByLastUpdatedComparer =
  (order: TaskSortingOrder) => (taskA: PreviewTask, taskB: PreviewTask) => {
    const dateA = new Date(taskA.updated_at).valueOf();
    const dateB = new Date(taskB.updated_at).valueOf();
    const result = dateA - dateB;
    return order === "asc" ? result : -result;
  };

export const getSortByDueDateComparer =
  (order: TaskSortingOrder) => (taskA: PreviewTask, taskB: PreviewTask) => {
    const hasDateA = Boolean(taskA.due_at);
    const hasDateB = Boolean(taskB.due_at);

    if (!hasDateA && !hasDateB) return 0;
    if (!hasDateA) return order === "asc" ? 1 : -1;
    if (!hasDateB) return order === "asc" ? -1 : 1;

    const dateA = new Date(taskA.due_at as string).valueOf();
    const dateB = new Date(taskB.due_at as string).valueOf();

    return order === "asc" ? dateA - dateB : dateB - dateA;
  };

export const getSortByCustomFieldComparer =
  (order: TaskSortingOrder, customField: TaskCustomFieldModel) =>
  (taskA: PreviewTask, taskB: PreviewTask) => {
    const valueA = taskA.custom_fields?.[customField.id] || "";
    const valueB = taskB.custom_fields?.[customField.id] || "";

    if (!valueA && !valueB) return 0;
    if (!valueA) return order === "asc" ? 1 : -1;
    if (!valueB) return order === "asc" ? -1 : 1;

    let result: number;

    switch (customField.type) {
      case "text":
        result = valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        break;
      case "number":
        {
          const numA = parseFloat(valueA) || 0;
          const numB = parseFloat(valueB) || 0;
          result = numA - numB;
        }
        break;
      case "date":
        {
          const dateA = new Date(valueA).valueOf();
          const dateB = new Date(valueB).valueOf();
          result = dateA - dateB;
        }
        break;
      case "dropdown":
        {
          const orderA =
            customField.values?.find((v) => v.id === valueA)?.order ?? 0;
          const orderB =
            customField.values?.find((v) => v.id === valueB)?.order ?? 0;
          result = orderA - orderB;
        }
        break;
      default:
        result = valueA.localeCompare(valueB);
        break;
    }

    return order === "asc" ? result : -result;
  };

type GetComparerForSortingRuleParams = {
  ruleType: TaskDefaultFieldId | "custom_field";
  order: TaskSortingOrder;
  getStatusByBoard: (
    boardId: string | null,
    statusId: string
  ) => TaskCustomStatusDto | undefined;
  customField?: TaskCustomFieldModel;
  labels?: TaskLabel[];
  boards?: TaskBoardModel[];
};

export const getComparerForSortingRule = ({
  ruleType,
  order,
  getStatusByBoard,
  customField,
  labels,
  boards,
}: GetComparerForSortingRuleParams) => {
  switch (ruleType) {
    case "due_date":
      return getSortByDueDateComparer(order);
    case "task_name":
      return getSortByTaskNameComparer(order);
    case "label":
      return getSortByLabelComparer(order, labels || []);
    case "status":
      return getSortByStatusNamesComparer(order, getStatusByBoard);
    case "priority":
      return getSortByPriorityComparer(order);
    case "activity":
      return getSortByLastUpdatedComparer(order);
    case "created_at":
      return getSortByCreatedAtComparer(order);
    case "updated_at":
      return getSortByUpdatedAtComparer(order);
    case "assignee":
      return getSortByAssigneeComparer(order);
    case "customer":
      return getSortByCustomerComparer(order);
    case "board":
      return getSortByBoardComparer(order, boards || []);
    case "custom_field":
      if (!customField) {
        throw new Error("Custom field is required for custom field sorting");
      }
      return getSortByCustomFieldComparer(order, customField);
    default:
      throw new Error(`Invalid sorting rule type: ${ruleType}`);
  }
};

export const isColumnSortable = (
  columnId: string,
  customFieldIds?: string[]
): boolean => {
  const sortableColumnMap: Record<string, TaskDefaultFieldId> = {
    task_name: "task_name",
    due_date: "due_date",
    created_at: "created_at",
    updated_at: "updated_at",
    label: "label",
    status: "status",
    priority: "priority",
    customer: "customer",
    board: "board",
  };

  if (columnId in sortableColumnMap) {
    return true;
  }

  if (customFieldIds && customFieldIds.includes(columnId)) {
    return true;
  }

  return false;
};

interface SortTasksByMultipleRulesParams {
  tasks: PreviewTask[];
  rules: TaskSorting;
  getStatusByBoard: (
    boardId: string | null,
    statusId: string
  ) => TaskCustomStatusDto | undefined;
  customFields?: TaskCustomFieldModel[];
  labels?: TaskLabel[];
  boards?: TaskBoardModel[];
}

export const sortTasksByMultipleRules = ({
  tasks,
  rules,
  customFields,
  labels,
  boards,
  getStatusByBoard,
}: SortTasksByMultipleRulesParams) => {
  if (rules.length === 0) {
    return tasks;
  }

  return [...tasks].sort((taskA, taskB) => {
    const firstNonZeroResult = rules
      .map((rule) => {
        if (rule.type === "custom_field" && rule.customFieldId) {
          const customField = customFields?.find(
            (cf) => cf.id === rule.customFieldId
          );
          if (!customField) return 0;
          return getComparerForSortingRule({
            ruleType: rule.type,
            order: rule.order,
            getStatusByBoard,
            customField,
            labels,
            boards,
          })(taskA, taskB);
        }
        return getComparerForSortingRule({
          ruleType: rule.type,
          order: rule.order,
          getStatusByBoard,
          labels,
          boards,
        })(taskA, taskB);
      })
      .find((result) => result !== 0);

    return firstNonZeroResult ?? 0;
  });
};

/**
 * Provide default sorting to keep task list order consistent (as backend doesn't do it)
 */
export const sortTasksInitially = (tasks: PreviewTask[]) => {
  const initialDateComparer = getSortByCreatedAtComparer("desc");
  const initialIdComparer = getSortByTaskIdComparer("desc");

  const initiallySortedTasks = [...tasks].sort((taskA, taskB) => {
    // Firstly, sort by creation date to imitate natural DB sorting (which backend does not do).
    const dateResult = initialDateComparer(taskA, taskB);

    if (dateResult !== 0) {
      return dateResult;
    }

    // Optionally, sort by task ID to maintain order within same creation date.
    return initialIdComparer(taskA, taskB);
  });

  return initiallySortedTasks;
};
